import { groupBy } from 'lodash';

import { collectMeetingEvents } from './meeting-debug.utils';

import type { MeetingDebugEvent } from '@electron/shared/meeting-debug.types';

export type MeetingDebugTone = 'ok' | 'warning' | 'error' | 'neutral';

export type MeetingDebugVerdict = {
  label: string;
  detail: string;
  tone: MeetingDebugTone;
};

export type MeetingDebugCard = {
  key: string;
  meetingId?: string;
  title: string;
  startedAt: number;
  endedAt?: number;
  tone: MeetingDebugTone;
  verdicts: MeetingDebugVerdict[];
  events: MeetingDebugEvent[];
};

export const SOURCE_LABELS: Record<MeetingDebugEvent['source'], string> = {
  contextkit: 'ContextKit',
  main: 'Main',
  renderer: 'Renderer',
};

const START_REASON_LABELS: Record<string, string> = {
  app_detection: 'meeting app detected',
  manual: 'started by hand',
  calendar_event: 'from the calendar',
  meeting_segment: 'moved to a new room',
  resume: 'resumed a stopped note',
};

const TRANSCRIPTION_ENGINE_LABELS: Record<string, string> = {
  remote: 'remote (cloud)',
  local: 'local (on this Mac)',
};

const NOTIFICATION_OUTCOME_LABELS: Record<string, string> = {
  user_start_recording_click: 'you clicked Start recording',
  timer_auto_start: 'started on its own when the timer ran out',
  user_dismiss: 'you dismissed the notification',
  timer_dismiss: 'the notification timed out',
  exclude_app: 'you excluded this app',
  exclude_website: 'you excluded this website',
  user_upgrade_click: 'you clicked Upgrade',
};

const STOP_LABELS: Record<string, { detail: string; tone: MeetingDebugTone }> =
  {
    stop_note_taking: { detail: 'stopped from the app', tone: 'ok' },
    inactivity_timeout: { detail: 'no speech for too long', tone: 'warning' },
    meeting_presence_lost: {
      detail: 'the call app went away',
      tone: 'warning',
    },
    new_meeting_started: { detail: 'switched to a new note', tone: 'ok' },
    sync_failure: { detail: 'transcript sync failed', tone: 'error' },
    backend_create_failed: {
      detail: 'the note could not be created',
      tone: 'error',
    },
    start_error: { detail: 'the start failed', tone: 'error' },
    'contextkit-crash': { detail: 'ContextKit crashed', tone: 'error' },
    transcriber_interrupt: {
      detail: 'the transcriber was interrupted',
      tone: 'error',
    },
    audio_start_failed: { detail: 'audio failed to start', tone: 'error' },
    meeting_limit_reached: { detail: 'meeting limit reached', tone: 'warning' },
    ipc_timeout: { detail: 'the app stopped responding', tone: 'error' },
    user_stopped: { detail: 'stopped from the app', tone: 'ok' },
    left_meeting: { detail: 'left the meeting', tone: 'ok' },
    tab_closed: { detail: 'the meeting tab closed', tone: 'ok' },
    system_suspended: { detail: 'the Mac went to sleep', tone: 'warning' },
    menu_toggle_stop: { detail: 'stopped from the menu bar', tone: 'ok' },
    menu_pause_stop: { detail: 'paused from the menu bar', tone: 'ok' },
    native_bridge_stop: { detail: 'stopped by the native bridge', tone: 'ok' },
    ipc_stop_observer: {
      detail: 'the app stopped ContextKit',
      tone: 'warning',
    },
    ipc_quit_process: { detail: 'the app quit', tone: 'warning' },
    error: { detail: 'ContextKit hit an error', tone: 'error' },
  };

const PRESENCE_CAUSE_LABELS: Record<string, string> = {
  no_apps_detected: 'no call app detected any more',
  bound_app_missing: 'the call app it was bound to went away',
};

const AUTO_START_EVENT_LABELS: Record<string, string> = {
  candidate_decision: 'Considered starting a note',
  dispatched: 'Showed the "meeting detected" notification',
  start_request_forwarded: 'Asked the main window to start',
  settings_changed: 'Auto-start setting changed',
};

const TONE_RANK: Record<MeetingDebugTone, number> = {
  neutral: 0,
  ok: 1,
  warning: 2,
  error: 3,
};

const humanize = (value: unknown) => String(value).replaceAll('_', ' ');

const lastOfKind = (
  events: MeetingDebugEvent[],
  kind: MeetingDebugEvent['kind']
) => events.filter((event) => event.kind === kind).at(-1);

const fieldText = (event: MeetingDebugEvent | undefined, key: string) => {
  const value = event?.fields[key];
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value : undefined;
};

const DETECTOR_RULE_LABELS: Record<string, string> = {
  same_process_input_and_output:
    'the same process is using the mic and speakers',
  no_recent_audio_ownership: 'no audio from it recently',
  empty_polls_threshold: 'no audio from it for several checks',
  input_only: 'it is only using the mic',
  output_only: 'it is only playing audio',
  input_and_output_in_different_processes:
    'mic and speakers are used by different processes',
};

function describeDetectorDecision(event: MeetingDebugEvent): string {
  const app = fieldText(event, 'app_name') || fieldText(event, 'app_bundle_id');
  const rule = fieldText(event, 'rule') ?? '';
  const ruleLabel = DETECTOR_RULE_LABELS[rule] ?? humanize(rule);
  const seconds = fieldText(event, 'seconds_since_audio_ownership');
  switch (fieldText(event, 'kind')) {
    case 'admitted':
      return `${app} detected: ${ruleLabel}`;
    case 'evicted':
      return `${app} dropped: ${ruleLabel}${seconds ? ` (${seconds}s since audio)` : ''}`;
    case 'not_admitted':
      return `${app} not detected: ${ruleLabel}`;
    case 'mic_active':
      return 'Microphone in use; checking meeting apps';
    case 'mic_inactive':
      return rule === 'empty_list_sent'
        ? 'Microphone quiet for 2 s; meeting apps cleared'
        : 'Microphone quiet for 2 s; no app had been reported, so nothing was sent';
    default:
      return event.summary;
  }
}

function describeContextKitEvent(event: MeetingDebugEvent): string {
  const text = (key: string) => humanize(fieldText(event, key) ?? 'unknown');
  switch (fieldText(event, 'event_type')) {
    case 'meeting_app_detector_decision':
      return describeDetectorDecision(event);
    case 'meeting_app_detector':
      return `Meeting detector ${fieldText(event, 'action') === 'stop' ? 'stopped' : 'started'}: ${text('trigger')}`;
    case 'meeting_detection_evaluation':
      return `Detection update not sent: ${text('ipc_withheld_reason')}`;
    case 'browser_gate_started_by_hand':
      return `Recording started by hand ${text('seconds_since_withheld')} s after a browser call was ignored: ${text('withheld_reason')}`;
    case 'browser_segmentation_evaluation':
      return `Browser call ignored: ${text('withheld_reason')}`;
    case 'startup_mic_check':
      return `Startup mic check: ${text('outcome')}`;
    case 'transcriber_recovery':
      return `Transcriber restarting: ${text('recovery_reason')}`;
    case 'transcriber_recovery_skipped':
      return `Transcriber restart skipped: ${text('skipped_reason')}`;
    case 'transcriber_recovery_override':
      return `Transcriber restart replaced: ${text('overriding_reason')}`;
    case 'transcriber_recovery_failure':
      return `Transcriber recovery failed: ${text('recovery_failure_type')}`;
    case 'local_transcription_startup_fallback':
      return `Fell back to ${text('new_active_transcription_engine')} transcription: ${text('fallback_reason')}`;
    default:
      return event.summary;
  }
}

export function describeEvent(event: MeetingDebugEvent): string {
  if (event.kind === 'contextkit_event') return describeContextKitEvent(event);
  if (event.kind === 'auto_start') {
    const eventType = fieldText(event, 'event_type') ?? '';
    const resolution = fieldText(event, 'resolution');
    if (resolution) {
      return `Notification: ${NOTIFICATION_OUTCOME_LABELS[resolution] ?? humanize(resolution)}`;
    }
    return AUTO_START_EVENT_LABELS[eventType] ?? humanize(eventType);
  }
  if (event.kind === 'stop') return `Stopped: ${stopVerdict(event).detail}`;
  return event.summary;
}

function stopVerdict(stop: MeetingDebugEvent): MeetingDebugVerdict {
  const type = fieldText(stop, 'type') ?? 'unknown';
  const label = STOP_LABELS[type] ?? { detail: humanize(type), tone: 'error' };
  const telemetry = stop.fields.telemetry as
    | Record<string, unknown>
    | undefined;
  const cause =
    typeof telemetry?.cause === 'string'
      ? PRESENCE_CAUSE_LABELS[telemetry.cause]
      : undefined;
  const timeoutMinutes = telemetry?.timeoutMinutes;
  const detail =
    cause ??
    (typeof timeoutMinutes === 'number'
      ? `no speech for ${timeoutMinutes} min`
      : label.detail);
  return { label: 'Stopped', detail, tone: label.tone };
}

function startVerdict(events: MeetingDebugEvent[]): MeetingDebugVerdict {
  const starts = events.filter((event) => event.kind === 'start_result');
  const start =
    starts.find((event) => fieldText(event, 'startReason') !== 'resume') ??
    starts[0];
  if (!start) {
    return { label: 'Started', detail: 'not recorded', tone: 'neutral' };
  }
  const reason = fieldText(start, 'startReason') ?? '';
  const reasonLabel = START_REASON_LABELS[reason] ?? humanize(reason);
  if (start.fields.outcome !== 'success') {
    const failure =
      fieldText(start, 'failureReason') ??
      fieldText(start, 'errorMessage') ??
      'unknown error';
    return {
      label: 'Start failed',
      detail: `${reasonLabel} · ${humanize(failure)}`,
      tone: 'error',
    };
  }
  const resolution = fieldText(
    events.find((event) => fieldText(event, 'resolution')),
    'resolution'
  );
  return {
    label: 'Started',
    detail: resolution
      ? `${reasonLabel} · ${NOTIFICATION_OUTCOME_LABELS[resolution] ?? humanize(resolution)}`
      : reasonLabel,
    tone: 'ok',
  };
}

function resumeVerdict(
  events: MeetingDebugEvent[]
): MeetingDebugVerdict | undefined {
  const resumes = events.filter(
    (event) =>
      event.kind === 'start_result' &&
      event.fields.outcome === 'success' &&
      fieldText(event, 'startReason') === 'resume'
  );
  if (resumes.length === 0) return undefined;
  return {
    label: 'Resumed',
    detail: resumes.length === 1 ? 'once' : `${resumes.length} times`,
    tone: 'ok',
  };
}

function transcriptionVerdict(
  events: MeetingDebugEvent[]
): MeetingDebugVerdict | undefined {
  const engines = events.flatMap((event) => {
    const engine =
      event.kind === 'start_result' && event.fields.outcome === 'success'
        ? fieldText(event, 'transcriptionEngine')
        : undefined;
    return engine ? [engine] : [];
  });
  const changes = engines.filter(
    (engine, index) => engine !== engines[index - 1]
  );
  if (changes.length === 0) return undefined;
  return {
    label: 'Transcription',
    detail: changes
      .map((engine) => TRANSCRIPTION_ENGINE_LABELS[engine] ?? engine)
      .join(', then '),
    tone: 'neutral',
  };
}

function postProcessingVerdict(
  events: MeetingDebugEvent[]
): MeetingDebugVerdict | undefined {
  const latest = lastOfKind(events, 'post_processing_state');
  if (!latest) return undefined;
  if (latest.fields.state === 'completed') {
    return { label: 'Post-processing', detail: 'completed', tone: 'ok' };
  }
  const failed = latest.fields.failed as { errorCode?: string } | undefined;
  return {
    label: 'Post-processing',
    detail: `failed · ${humanize(failed?.errorCode ?? 'unknown')}`,
    tone: 'error',
  };
}

function detectedAppLabel(events: MeetingDebugEvent[]): string | undefined {
  const detection = events.find(
    (event) =>
      event.kind === 'apps_updated' &&
      Array.isArray(event.fields.apps) &&
      event.fields.apps.length > 0
  );
  const app = (
    detection?.fields.apps as
      | { name?: string; websiteHost?: string }[]
      | undefined
  )?.[0];
  if (!app) return undefined;
  return app.websiteHost ? `${app.websiteHost} in ${app.name}` : app.name;
}

const worstTone = (verdicts: MeetingDebugVerdict[]) =>
  verdicts.reduce<MeetingDebugTone>(
    (worst, verdict) =>
      TONE_RANK[verdict.tone] > TONE_RANK[worst] ? verdict.tone : worst,
    'neutral'
  );

function buildMeetingCard(
  meetingId: string,
  meetingEvents: MeetingDebugEvent[],
  globalEvents: MeetingDebugEvent[]
): MeetingDebugCard {
  const events = collectMeetingEvents(meetingEvents, globalEvents);
  const latestStart = events
    .filter(
      (event) =>
        event.kind === 'start_result' && event.fields.outcome === 'success'
    )
    .at(-1);
  // Only stops after the latest start count; ContextKit's stop is the only one for sleep and quit
  const stops = events.filter(
    (event) =>
      event.kind === 'stop' && (!latestStart || event.at > latestStart.at)
  );
  const stop =
    stops.filter((event) => event.source === 'renderer').at(-1) ?? stops.at(-1);
  const verdicts = [
    startVerdict(events),
    resumeVerdict(events),
    transcriptionVerdict(events),
    stop
      ? stopVerdict(stop)
      : {
          label: 'Stopped',
          detail: 'still recording',
          tone: 'neutral' as const,
        },
    postProcessingVerdict(events),
  ].filter((verdict) => verdict !== undefined);

  return {
    key: meetingId,
    meetingId,
    title: detectedAppLabel(events) ?? 'Meeting note',
    startedAt: events[0]?.at ?? 0,
    endedAt: stop?.at,
    tone: worstTone(verdicts),
    verdicts,
    events,
  };
}

function hasDetectedApps(event: MeetingDebugEvent) {
  return Array.isArray(event.fields.apps) && event.fields.apps.length > 0;
}

/** A detection that never became a note: what was seen, and why nothing started */
function buildUnstartedCard(
  key: string,
  events: MeetingDebugEvent[]
): MeetingDebugCard | undefined {
  const isOnlyEmptyDetections = events.every(
    (event) => event.kind === 'apps_updated' && !hasDetectedApps(event)
  );
  if (isOnlyEmptyDetections) return undefined;

  const skip = lastOfKind(events, 'skipped');
  const resolution = fieldText(
    events.filter((event) => fieldText(event, 'resolution')).at(-1),
    'resolution'
  );
  const outcome: MeetingDebugVerdict = skip
    ? {
        label: 'Skipped',
        detail: humanize(skip.fields.reason),
        tone: 'warning',
      }
    : resolution
      ? {
          label: 'Not started',
          detail:
            NOTIFICATION_OUTCOME_LABELS[resolution] ?? humanize(resolution),
          tone: 'neutral',
        }
      : {
          label: 'Not started',
          detail: 'no decision recorded',
          tone: 'neutral',
        };

  const calendarTitle = fieldText(skip, 'title');
  return {
    key,
    title: calendarTitle
      ? `Calendar: ${calendarTitle}`
      : `Detected ${detectedAppLabel(events) ?? 'a meeting'}, not started`,
    startedAt: events[0].at,
    tone: outcome.tone,
    verdicts: [outcome],
    events,
  };
}

/** Newest first: meeting notes and the detections or reminders that started none, then everything else */
export function buildMeetingDebugCards(
  eventsByMeetingId: Map<string, MeetingDebugEvent[]>,
  globalEvents: MeetingDebugEvent[]
): MeetingDebugCard[] {
  const meetingCards = [...eventsByMeetingId].map(([meetingId, events]) =>
    buildMeetingCard(meetingId, events, globalEvents)
  );
  const adopted = new Set(meetingCards.flatMap((card) => card.events));
  const unadopted = globalEvents.filter((event) => !adopted.has(event));
  const { other = [], ...groups } = groupBy(
    unadopted,
    (event) => event.detectionId ?? fieldText(event, 'eventId') ?? 'other'
  );
  const unstartedCards = Object.entries(groups).flatMap(([key, events]) => {
    const card = buildUnstartedCard(key, events);
    return card ? [card] : [];
  });
  const otherCard: MeetingDebugCard[] =
    other.length > 0
      ? [
          {
            key: 'other',
            title: 'Background events',
            startedAt: other.at(-1)?.at ?? 0,
            tone: 'neutral',
            verdicts: [],
            events: other,
          },
        ]
      : [];

  return [
    ...[...meetingCards, ...unstartedCards].sort(
      (a, b) => b.startedAt - a.startedAt
    ),
    ...otherCard,
  ];
}

export function formatMeetingDebugReport(
  card: MeetingDebugCard,
  title: string
): string {
  const lines = [
    `Meeting debug log: ${title}`,
    `Meeting id: ${card.meetingId ?? 'none (no note started)'}`,
    `From ${new Date(card.startedAt).toISOString()}${card.endedAt ? ` to ${new Date(card.endedAt).toISOString()}` : ''}`,
    '',
    ...card.verdicts.map((verdict) => `${verdict.label}: ${verdict.detail}`),
    '',
    'Steps',
    ...card.events.map(
      (event) =>
        `${new Date(event.at).toISOString()}  ${SOURCE_LABELS[event.source].padEnd(10)}  ${describeEvent(event)}`
    ),
    '',
    'Raw entries',
    ...card.events.map((event) => JSON.stringify(event)),
  ];
  return `${lines.join('\n')}\n`;
}
